import type { BusinessUnitLeadMetrics } from 'services/types/metrics';

import createNavigationHandler from '../navigationHandler';

interface MetricData {
  type: string;
  data: number;
  secondaryData?: number;
  tooltip: string;
  percentage?: boolean;
  secondaryDataPercentage?: boolean;
  onActionButtonClick?: () => void;
  navigationTooltip?: string;
}

/**
 * Gets small hard metrics for leads to display in metric cards (4 columns layout)
 *
 * @param {Object} props - The props object
 * @param {BusinessUnitLeadMetrics | null} props.businessUnitLeadMetrics - Lead metrics data
 * @param {Function} props.onActionButtonClick - Navigation function to be called when button is clicked
 * @param {Object} props.filters - Current filters to include in navigation URLs
 * @returns {Array} Array of metric objects for display
 */
const getLeadSmallMetrics = ({
  businessUnitLeadMetrics,
  totalTemplateMessages,
  onActionButtonClick,
  filters,
  hasSellerManagement,
}: {
  businessUnitLeadMetrics: BusinessUnitLeadMetrics | null;
  totalTemplateMessages?: number;
  onActionButtonClick: (url: string) => void;
  filters: {
    startDate: Date;
    endDate: Date;
    businessUnitId: number;
    sources?: string[];
    brands?: string[];
    campaignIds?: string[];
  };
  hasSellerManagement?: boolean;
}): MetricData[] => {
  if (!businessUnitLeadMetrics) {
    return [];
  }

  const { hardMetrics } = businessUnitLeadMetrics;
  const leadsContacted = hardMetrics.leadsContacted || 0;

  const metrics: MetricData[] = [
    {
      type: 'Templates Enviados',
      data: Number(totalTemplateMessages || 0),
      tooltip: 'Número total de mensajes enviados mediante un template (plantilla) de WhatsApp',
      onActionButtonClick: createNavigationHandler({
        onActionButtonClick,
        filters,
        additionalFilters: { templatesSentCount: true },
      }),
      navigationTooltip: 'Ir a la vista de leads y filtrar los leads que tienen plantillas enviadas',
    },
    {
      type: 'Leads Fuera de Horario Laboral',
      data: Number(leadsContacted > 0 ? (hardMetrics.leadsOutsideWorkingHours || 0) / leadsContacted : 0),
      secondaryData: Number(hardMetrics.leadsOutsideWorkingHours || 0),
      tooltip: 'Leads creados fuera del horario laboral (lunes a viernes de 9 a 18 hrs)',
      percentage: true,
      onActionButtonClick: createNavigationHandler({
        onActionButtonClick,
        filters,
        additionalFilters: { outsideWorkingHours: true },
      }),
      navigationTooltip: 'Ir a la vista de leads creados fuera del horario laboral',
    },
  ];

  if (hasSellerManagement) {
    metrics.push({
      type: 'Asignados a Ejecutivo',
      data: Number(leadsContacted > 0 ? (hardMetrics.leadsConverted || 0) / leadsContacted : 0),
      secondaryData: Number(hardMetrics.leadsConverted || 0),
      tooltip:
        'Leads que están siendo atendidos por un ejecutivo o están esperando a ser atendidos. Se consideran solo los leads contactados',
      percentage: true,
      onActionButtonClick: createNavigationHandler({
        onActionButtonClick,
        filters,
        additionalFilters: { isAssignedToExecutive: true },
      }),
      navigationTooltip: 'Ir a la vista de leads asignados a un ejecutivo',
    });
  }

  return metrics;
};

export default getLeadSmallMetrics;
